import React, { ReactNode } from 'react';
import { ChatHeader } from './chat-header';
import { ChatMessages } from './chat-messages';
import { SourcesPanel } from './sources-panel';
import { Source } from '../lib/sources-utils';

interface ChatSessionLayoutProps {
  chatName: string;
  vehicleName?: string;
  showSidebar: boolean; // parent controls actual sidebar component rendering
  onToggleSidebar: () => void;
  showSources: boolean;
  onToggleSources: () => void;
  sources: Source[];
  highlightedSourceId?: string;
  onSourceHighlight?: (sourceId?: string) => void;
  messages: any[]; // Using any to avoid tight coupling; underlying component expects Message[]
  streamedMessage?: string;
  isLoading?: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  banner?: ReactNode; // Optional top banner (e.g., preview notice)
  footer?: ReactNode; // Optional footer (e.g., ChatInput)
  chatId?: string;
  onSourceClick?: (source: Source) => void;
}

/**
 * Reusable layout for chat-oriented pages.
 * Parent is responsible for rendering the Sidebar sibling element.
 */
export const ChatSessionLayout: React.FC<ChatSessionLayoutProps> = ({
  chatName,
  vehicleName = '',
  showSidebar: _showSidebar, // just to keep api parity if needed later
  onToggleSidebar,
  showSources,
  onToggleSources,
  sources,
  highlightedSourceId,
  messages,
  streamedMessage,
  isLoading,
  messagesEndRef,
  banner,
  footer,
  chatId,
  onSourceClick
}) => {
  const isDev = process.env.NODE_ENV !== 'production';
  const messagesIsArray = Array.isArray(messages);
  const messagesCount = messagesIsArray ? (messages as any[]).length : 0;
  return (
    <main className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
      <ChatHeader
        chatName={chatName}
        vehicle={vehicleName}
        onToggleSidebar={onToggleSidebar}
        onToggleSources={onToggleSources}
        sourcesOpen={showSources}
        sourcesCount={sources.length}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 md:p-6">
          {banner}

          
          <ChatMessages
            messages={messages as any}
            streamedMessage={streamedMessage}
            messagesEndRef={messagesEndRef}
            isLoading={isLoading}
            chatId={chatId}
            sources={sources}
            onSourceClick={onSourceClick}
          />
        </div>
      </div>

      {footer}

      <SourcesPanel
        sources={sources}
        isOpen={showSources}
        onToggle={onToggleSources}
        onClose={() => onToggleSources()}
        highlightedSourceId={highlightedSourceId}
        onSourceClick={(source) => onSourceClick && onSourceClick(source)}
      />
    </main>
  );
};
